import type { NavIconName } from './navigation.ts';

/**
 * The sidebar's glyph set.
 *
 * These used to be single `d` strings held on the nav data itself, and one path per icon is what
 * made half of them unreadable at 18px: the dashboard was a compound rectangle path *stroked*, so
 * its subpaths overlapped into a blob, and settings was a sun standing in for a gear. Several
 * needed two or three shapes to be the thing they were naming, which a lone `d` cannot hold.
 *
 * Named rather than passed as path data, so the artwork lives in one file and the navigation stays
 * a list of destinations. One 24-unit grid, one stroke weight, `currentColor` throughout — the
 * sidebar decides the tone and a glyph never carries a colour of its own.
 *
 * All decorative: every one sits beside its own label, so they are out of the accessibility tree
 * rather than announced twice.
 */
export function NavIcon({ name }: { name: NavIconName }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {GLYPHS[name]}
    </svg>
  );
}

const GLYPHS: Record<NavIconName, React.ReactNode> = {
  /*
   * A gauge, not a grid of panes. The four-pane glyph this replaces was the same shape as
   * Categories two rows below it, so the two top entries of the menu were telling each other
   * apart by their labels alone.
   */
  dashboard: (
    <>
      <path d="M4.5 17.6a8.5 8.5 0 1115 0" />
      <path d="M12 17.6l3.9-4.7" />
      <circle cx="12" cy="17.6" r="1.3" />
    </>
  ),
  /** A coffee pouch under its crimp — what this shop actually sells. */
  products: (
    <>
      <rect x="7" y="3.4" width="10" height="2.7" rx="1.35" />
      <path d="M8 6.1h8l.7 12.3a2 2 0 01-2 2.1H9.3a2 2 0 01-2-2.1L8 6.1z" />
    </>
  ),
  categories: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1.6" />
      <rect x="13" y="4" width="7" height="7" rx="1.6" />
      <rect x="4" y="13" width="7" height="7" rx="1.6" />
      <rect x="13" y="13" width="7" height="7" rx="1.6" />
    </>
  ),
  /** A parcel on the move. A bag would be a second pouch, and Products already has that. */
  orders: (
    <>
      <path d="M12 3.2l7.5 4v9.6l-7.5 4-7.5-4V7.2l7.5-4z" />
      <path d="M4.5 7.2l7.5 4 7.5-4M12 11.2v9.6" />
    </>
  ),
  /** A swing tag with its punched hole: a price that has been marked down. */
  coupons: (
    <>
      <path d="M11.4 4.2H19a.8.8 0 01.8.8v7.6a2 2 0 01-.6 1.4l-5.4 5.4a1.6 1.6 0 01-2.3 0l-6.7-6.7a1.6 1.6 0 010-2.3L10.2 5a2 2 0 011.2-.8z" />
      <circle cx="15.4" cy="8.6" r="1.3" />
    </>
  ),
  /** Two people, so it is a customer list rather than one profile. */
  customers: (
    <>
      <circle cx="9.5" cy="8.6" r="3.3" />
      <path d="M3.6 19.6a5.9 5.9 0 0111.8 0" />
      <path d="M15.8 6.3a3.3 3.3 0 010 4.6M17.2 14.8a5.6 5.6 0 013.2 4.8" />
    </>
  ),
  reviews: (
    <path d="M12 4l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4L4.2 9.7l5.4-.8L12 4z" />
  ),
  /** A bubble with a tail and something written in it. */
  inquiries: (
    <>
      <path d="M20 14a2.5 2.5 0 01-2.5 2.5H9.2L5 20V7a2.5 2.5 0 012.5-2.5h10A2.5 2.5 0 0120 7v7z" />
      <path d="M8.8 9h7M8.8 12.4h4.4" opacity="0.55" />
    </>
  ),
  /** A page with a heading and copy under it, which is what a content block is. */
  content: (
    <>
      <rect x="4.5" y="3.5" width="15" height="17" rx="2.2" />
      <path d="M8 8.4h8" />
      <path d="M8 12.2h8M8 15.9h5" opacity="0.55" />
    </>
  ),
  /*
   * Sliders, not a gear. A cog's teeth need more strokes than 18px has room for and it came out
   * as a starburst; two sliders read as "settings" at any size.
   */
  settings: (
    <>
      <path d="M4 8h9M18.5 8H20" />
      <circle cx="15.8" cy="8" r="2.2" />
      <path d="M4 16h3.7M12.5 16H20" />
      <circle cx="10.1" cy="16" r="2.2" />
    </>
  ),
};
